import { type } from 'arktype';
import { confirmGateFields, dateKey } from '@zag/core/models';
import type { RuleLayer } from './rule-catalog.model';

/**
 * Rule-bundle models (F20 org-chart-position-editor, Story 2 / Phase 3a):
 * a named bundle of GAV/reglement rule instances + curated roles ("Regelwerk"),
 * attached effective-dated to org units XOR positions.
 */

const uuidString = type('string.uuid').configure({ description: 'UUID.' });

export const createRuleBundleSchema = type({
  name: type('string > 0').configure({ description: 'Display name of the bundle.', examples: ['GAV Gastro Reglement'] }),
  'description?': type('string | null').configure({ description: 'Optional description.' }),
  'color?': type('string | null').configure({ description: 'Palette chip colour (design token key or hex), UI only.' }),
  'rules?': type('string.uuid[]').configure({
    description: 'Member rule-instance ids — GAV and reglement instances are allowed; statutory is rejected (422).',
  }),
  'roles?': type('string[]').configure({
    description: 'Member role ids — only roles flagged assignableToPositions are allowed (422).',
  }),
}).configure({ description: 'Payload to create a rule bundle (rules + roles as full member sets).' });

export type CreateRuleBundle = typeof createRuleBundleSchema.t;

export const updateRuleBundleSchema = type({
  'name?': type('string > 0').configure({ description: 'New display name.' }),
  'description?': type('string | null').configure({ description: 'New description.' }),
  'color?': type('string | null').configure({ description: 'New palette chip colour.' }),
  'rules?': type('string.uuid[]').configure({
    description: 'FULL replacement set of member rule-instance ids — GAV and reglement instances are allowed; statutory is rejected (422).',
  }),
  'roles?': type('string[]').configure({
    description:
      'FULL replacement set of member role ids — only assignableToPositions roles allowed (422). Adding roles while the bundle ' +
      'has active assignments runs the self-escalation lock (403 when your own position lies in an assigned scope).',
  }),
  ...confirmGateFields,
}).configure({
  description:
    'Partial update of a rule bundle; `rules`/`roles` replace the whole member set when present. Changing either while an ' +
    'active assignment reaches into the past runs the past-impact confirm gate (409/403, spec Story 4).',
});

export type UpdateRuleBundle = typeof updateRuleBundleSchema.t;

export const createRuleBundleAssignmentSchema = type({
  'orgUnitId?': uuidString.or(type('null')).configure({ description: 'Target org unit (inherited by the subtree); XOR with positionId.' }),
  'positionId?': uuidString.or(type('null')).configure({ description: 'Target position (fine-grained); XOR with orgUnitId.' }),
  validFrom: dateKey.configure({ description: 'First day (inclusive) the bundle assignment applies.' }),
  ...confirmGateFields,
}).configure({ description: 'Attach a rule bundle to exactly one org unit XOR position, effective-dated.' });

export type CreateRuleBundleAssignment = typeof createRuleBundleAssignmentSchema.t;

export const endRuleBundleAssignmentSchema = type({
  endOn: dateKey.configure({ description: 'Last day (inclusive) of the bundle assignment — the row is closed, never deleted.' }),
  ...confirmGateFields,
}).configure({ description: 'End a bundle assignment at a key date (append-only).' });

export type EndRuleBundleAssignment = typeof endRuleBundleAssignmentSchema.t;

export const ruleBundleAssignmentListQuerySchema = type({
  'orgUnitId?': uuidString.configure({ description: 'Filter assignments by target org unit.' }),
  'positionId?': uuidString.configure({ description: 'Filter assignments by target position.' }),
  'bundleId?': uuidString.configure({ description: 'Filter assignments by bundle id.' }),
}).configure({
  description:
    'Query parameters for the bundle-assignment history (F20 Phase 4b: resolves the assignment id of a badge shown on the ' +
    'org-chart tree, which only carries `bundleId` — needed by the Inspector to end a direct assignment).',
});

export type RuleBundleAssignmentListQuery = typeof ruleBundleAssignmentListQuerySchema.t;

// ---------------------------------------------------------------------------
// Response DTOs
// ---------------------------------------------------------------------------

/** Member rule instance of a bundle (detail projection for the list/editor). */
export interface RuleBundleRuleDetail {
  ruleInstanceId: string;
  kindKey: string;
  layer: RuleLayer;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
}

/** Member role of a bundle (detail projection for the list/editor). */
export interface RuleBundleRoleDetail {
  roleId: string;
  description: string | null;
}

/** A rule bundle with resolved rule/role details. */
export interface RuleBundleDto {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  /** ISO timestamp; null = not archived. */
  archivedAt: string | null;
  rules: RuleBundleRuleDetail[];
  roles: RuleBundleRoleDetail[];
  /** Assignments overlapping [server-today, ∞) — drives the delete/archive UI. */
  activeAssignmentCount: number;
}

/** Effective-dated bundle assignment to an org unit XOR position. */
export interface RuleBundleAssignmentDto {
  id: string;
  bundleId: string;
  orgUnitId: string | null;
  positionId: string | null;
  validFrom: string;
  validTo: string | null;
}

/**
 * One duplicate hit of a bundle assignment (spec Story 2, Muster §3d-21):
 * a rule of the assigned bundle (same `kindKey`, overlapping validity)
 * already acts in the target scope — via another bundle assignment
 * (`source: 'bundle'`) or as a tenant-global reglement instance that is not
 * a member of any bundle (`source: 'direct'`). A WARNING, never a block.
 */
export interface BundleDuplicateWarningEntry {
  kindKey: string;
  /** Rule instance of the ASSIGNED bundle that is duplicated. */
  candidateRuleInstanceId: string;
  /** Rule instance that already acts in the scope. */
  conflictRuleInstanceId: string;
  source: 'bundle' | 'direct';
  /** Bundle carrying the conflicting rule (`source: 'bundle'` only). */
  conflictBundleId: string | null;
  /** Overlapping assignment of the conflicting bundle (`source: 'bundle'` only). */
  conflictAssignmentId: string | null;
}

/** Result of a bundle-assignment write incl. the duplicate warning (spec Story 2). */
export interface RuleBundleAssignmentWriteResult {
  assignment: RuleBundleAssignmentDto;
  /** Non-empty array when duplicates were detected; null = clean. */
  duplicateWarning: BundleDuplicateWarningEntry[] | null;
}
